import {BaseApi} from './BaseApi'

// ============ Reference Data Types ============

export interface PositionReference {
  id: string
  name: string
  companyId: string
  siteId: string
  departmentId: string
  billCodeId: string
  wcCodeId: string
  payRateMin: number
  payRateMax: number
  payType: string
}

export interface BillCodeReference {
  id: string
  name: string
  companyId: string
  wcCodeId: string
  status: string
  state: string
}

export interface WCCodeReference {
  id: string
  wcCode: string
  state: string
  description: string
}

export interface CompanyReference {
  id: string
  name: string
  status: string
}

export interface SiteReference {
  id: string
  name: string
  parentId: string
  parentType: string
  ronosCompanyId?: number
}

export interface DepartmentReference {
  id: string
  name: string
  siteId: string
  siteName: string
  ronosDepartmentId?: number
}

export interface ApplicantReference {
  id: string
  employeeId: string
  firstName: string
  lastName: string
  last4ssn: string
  employeeStatus: string
}

export interface AssignmentMigrationReferenceData {
  agencyId: string
  agencyName: string
  positions: PositionReference[]
  billCodes: BillCodeReference[]
  wcCodes: WCCodeReference[]
  companies: CompanyReference[]
  sites: SiteReference[]
  departments: DepartmentReference[]
  applicants: ApplicantReference[]
}

// ============ Import Result Types ============

export interface AssignmentImportResult {
  rowNumber: number
  success: boolean
  assignmentId?: string
  serviceOrderId?: string
  jobOrderId?: string
  ronosSyncStatus?: string
  error?: string
  validationErrors?: string[]
}

export interface AssignmentMigrationResult {
  totalAttempted: number
  totalSuccessful: number
  totalFailed: number
  results: AssignmentImportResult[]
  serviceOrdersCreated: number
  jobOrdersCreated: number
  processingTimeMs: number
}

/**
 * Data Migration API service
 * Handles assignment migration operations for platform admins
 */
export class DataMigrationApi extends BaseApi {
  /**
   * Get reference data for assignment migration
   * GET /data-migration/assignments/reference-data/:agencyId
   */
  async getAssignmentReferenceData(agencyId: string): Promise<AssignmentMigrationReferenceData> {
    try {
      const response = await this.client.get<AssignmentMigrationReferenceData>(
        `/data-migration/assignments/reference-data/${agencyId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching assignment migration reference data:', agencyId, error)
      throw error
    }
  }

  /**
   * Download assignment migration template for an agency
   * GET /data-migration/assignments/template/:agencyId
   * Returns CSV as a Blob
   */
  async downloadAssignmentTemplate(agencyId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/data-migration/assignments/template/${agencyId}`, {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error downloading assignment migration template:', agencyId, error)
      throw error
    }
  }

  /**
   * Download empty assignment migration template
   * GET /data-migration/assignments/template
   * Returns CSV as a Blob
   */
  async downloadEmptyAssignmentTemplate(): Promise<Blob> {
    try {
      const response = await this.client.get('/data-migration/assignments/template', {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error downloading empty assignment migration template:', error)
      throw error
    }
  }

  /**
   * Import assignments from a CSV file
   * POST /data-migration/assignments/:agencyId
   */
  async importAssignments(agencyId: string, file: File): Promise<AssignmentMigrationResult> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await this.client.post<AssignmentMigrationResult>(
        `/data-migration/assignments/${agencyId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error importing assignments:', agencyId, error)
      throw error
    }
  }

  /**
   * Helper to trigger file download in browser
   */
  triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}

export const dataMigrationApi = new DataMigrationApi()

